import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BranchDto,
  BrandAliasDto,
  BrandDto,
  DiscountDto,
  ListResponse,
  RateCardDto,
  SupplierDto,
} from "@reina/shared";
import type {
  Branch,
  BranchInput,
  BrandAliasInput,
  BrandInput,
  DiscountInput,
  RateCardInput,
  SupplierInput,
} from "@reina/shared";
import { api } from "./client";

/**
 * TanStack Query hooks for the catalogue routes (see app/API-CONTRACT.md).
 * Key layout: `[entity, "list", params]` / `[entity, "count"]` /
 * `["suppliers", "branches", supplierId]`, so invalidating `[entity]` sweeps
 * lists, counts and children in one go.
 */

const SupplierList = ListResponse(SupplierDto);
const BranchList = ListResponse(BranchDto);
const BrandList = ListResponse(BrandDto);
const RateCardList = ListResponse(RateCardDto);
const DiscountList = ListResponse(DiscountDto);

export type ListParams = {
  limit?: number;
  offset?: number;
};

/** Big-enough page for pilot-sized catalogue lists (contract max is 500). */
export const CATALOGUE_PAGE = { limit: 500, offset: 0 } satisfies ListParams;

// ---------------------------------------------------------------------------
// Suppliers & branches
// ---------------------------------------------------------------------------

export function useSuppliers(params: ListParams = CATALOGUE_PAGE) {
  return useQuery({
    queryKey: ["suppliers", "list", params],
    queryFn: () => api.get("/suppliers", { query: params, schema: SupplierList }),
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SupplierInput) => api.post("/suppliers", input, { schema: SupplierDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["suppliers"] }),
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<SupplierInput> }) =>
      api.patch(`/suppliers/${id}`, input, { schema: SupplierDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["suppliers"] }),
  });
}

const branchesKey = (supplierId: string) => ["suppliers", "branches", supplierId] as const;
const fetchBranches = (supplierId: string) =>
  api.get(`/suppliers/${supplierId}/branches`, { schema: BranchList });

export function useBranches(supplierId: string | null) {
  return useQuery({
    queryKey: branchesKey(supplierId ?? "none"),
    queryFn: () => fetchBranches(supplierId ?? ""),
    enabled: supplierId !== null,
  });
}

/**
 * Branch lists for a set of suppliers, combined into `supplierId → count`.
 * `SupplierDto` carries no branch count, so the supplier table fans out one
 * request per row (fine at pilot catalogue size; results are cached per id).
 */
export function useBranchCounts(supplierIds: string[]) {
  return useQueries({
    queries: supplierIds.map((id) => ({
      queryKey: branchesKey(id),
      queryFn: () => fetchBranches(id),
    })),
    combine: (results): Record<string, number> => {
      const counts: Record<string, number> = {};
      results.forEach((result, index) => {
        const id = supplierIds[index];
        if (id !== undefined && result.data) counts[id] = result.data.total;
      });
      return counts;
    },
  });
}

export function useCreateBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, input }: { supplierId: string; input: BranchInput }) =>
      api.post(`/suppliers/${supplierId}/branches`, input, { schema: BranchDto }),
    onSuccess: (_data: Branch, { supplierId }) =>
      queryClient.invalidateQueries({ queryKey: branchesKey(supplierId) }),
  });
}

export function useUpdateBranch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      supplierId,
      branchId,
      input,
    }: {
      supplierId: string;
      branchId: string;
      input: Partial<BranchInput>;
    }) =>
      api.patch(`/suppliers/${supplierId}/branches/${branchId}`, input, { schema: BranchDto }),
    onSuccess: (_data, { supplierId }) =>
      queryClient.invalidateQueries({ queryKey: branchesKey(supplierId) }),
  });
}

// ---------------------------------------------------------------------------
// Brands & aliases
// ---------------------------------------------------------------------------

export function useBrands(params: ListParams = CATALOGUE_PAGE) {
  return useQuery({
    queryKey: ["brands", "list", params],
    queryFn: () => api.get("/brands", { query: params, schema: BrandList }),
  });
}

export function useCreateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BrandInput) => api.post("/brands", input, { schema: BrandDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useUpdateBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<BrandInput> }) =>
      api.patch(`/brands/${id}`, input, { schema: BrandDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useAddBrandAlias() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, input }: { brandId: string; input: BrandAliasInput }) =>
      api.post(`/brands/${brandId}/aliases`, input, { schema: BrandAliasDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useRemoveBrandAlias() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, aliasId }: { brandId: string; aliasId: string }) =>
      api.del(`/brands/${brandId}/aliases/${aliasId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["brands"] }),
  });
}

// ---------------------------------------------------------------------------
// Rate cards
// ---------------------------------------------------------------------------

export function useRateCards(params: ListParams & { supplierId?: string } = CATALOGUE_PAGE) {
  return useQuery({
    queryKey: ["rate-cards", "list", params],
    queryFn: () => api.get("/rate-cards", { query: params, schema: RateCardList }),
  });
}

export function useCreateRateCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RateCardInput) => api.post("/rate-cards", input, { schema: RateCardDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["rate-cards"] }),
  });
}

export function useUpdateRateCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<RateCardInput> }) =>
      api.patch(`/rate-cards/${id}`, input, { schema: RateCardDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["rate-cards"] }),
  });
}

// ---------------------------------------------------------------------------
// Discounts
// ---------------------------------------------------------------------------

export function useDiscounts(params: ListParams & { supplierId?: string } = CATALOGUE_PAGE) {
  return useQuery({
    queryKey: ["discounts", "list", params],
    queryFn: () => api.get("/discounts", { query: params, schema: DiscountList }),
  });
}

export function useCreateDiscount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DiscountInput) => api.post("/discounts", input, { schema: DiscountDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["discounts"] }),
  });
}

export function useUpdateDiscount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<DiscountInput> }) =>
      api.patch(`/discounts/${id}`, input, { schema: DiscountDto }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["discounts"] }),
  });
}
